import React, { useState } from 'react';
import { Table, Tag, Button, Space, Modal, Alert, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { formatCurrency, formatDate, getCashFlowStatusColor, getCashFlowStatusText, truncate } from 'utility/utility';
import { CashFlowStatus, PaymentMethod } from 'api/models';
import dayjs from 'dayjs';
import { DealsApi } from 'api/api';
import openNotificationWithIcon from 'utility/notification';

const getPaymentMethodIcon = (method: PaymentMethod) => {
  switch (method) {
    case PaymentMethod.Cash: // Cash
      return 'dollar-sign';
    case PaymentMethod.Bank: // Bank
      return 'credit-card';
    case PaymentMethod.Crypto: // Crypto
      return 'shield';
    case PaymentMethod.Other: // Other
      return 'help-circle';
    default:
      return 'help-circle';
  }
};

interface Props {
  payments?: any[];
  onRecordPayment: (paymentId: number) => void;
  disabled?: boolean;
  onRefresh?: () => void;
}

const dealsApi = new DealsApi();
const PaymentSchedule: React.FC<Props> = ({ payments = [], onRecordPayment, disabled, onRefresh }) => {
  const { t } = useTranslation();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeletePayment = async () => {
    if (!selectedPaymentId) return;

    setIsDeleting(true);
    try {
      await dealsApi.apiDealsPaymentsIdDelete({
        id: selectedPaymentId,
        deleteDealInstallmentCommand: {
          cashFlowId: selectedPaymentId,
          notes: deleteReason,
        },
      });
      openNotificationWithIcon('success', t('payments:payment-deleted', 'Payment deleted successfully'));
      onRefresh?.();
    } catch (error) {
      openNotificationWithIcon('error', t('payments:delete-failed', 'Failed to delete payment'));
      console.error('Failed to delete payment:', error);
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setDeleteReason('');
      setSelectedPaymentId(null);
    }
  };

  const actionColumn = {
    title: t('global:actions', 'Actions'),
    key: 'actions',
    render: (record: any) => (
      <Space>
        {!record.paidAmount && record.status != CashFlowStatus.Cancelled && !disabled && (
          <Button
            type="primary"
            size="small"
            onClick={() => onRecordPayment(record.id)}
            icon={<FeatherIcon icon="check-circle" size={16} />}
          >
            {t('payments:record-payment', 'Record Payment')}
          </Button>
        )}
        {record.status != CashFlowStatus.Cancelled && !disabled && (
          <Button
            type="primary"
            danger
            size="small"
            onClick={() => {
              setSelectedPaymentId(record.id);
              setDeleteModalVisible(true);
            }}
            icon={<FeatherIcon icon="trash-2" size={16} />}
          >
            {t('payments:delete-payment', 'Delete')}
          </Button>
        )}
      </Space>
    ),
  };

  const columns = [
    {
      title: t('payments:due-date', 'Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'), // Adjust format if needed
      sorter: (a: any, b: any) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf(),
    },
    {
      title: t('payments:expected-amount', 'Expected Amount'),
      dataIndex: 'expectedAmount',
      key: 'expectedAmount',
      render: (amount: number) => formatCurrency(amount),
      sorter: (a: any, b: any) => a.expectedAmount - b.expectedAmount,
    },
    {
      title: t('payments:paid-amount', 'Paid Amount'),
      key: 'paidAmount',
      render: (record: any) => (
        <div>
          {record.paidAmount ? (
            <>
              <div>{formatCurrency(record.paidAmount)}</div>
              <div className="text-xs text-gray-500">{dayjs(record.paidDate).format('YYYY-MM-DD')}</div>
            </>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      title: t('payments:status', 'Status'),
      key: 'status',
      render: (record: any) => (
        <Tag color={getCashFlowStatusColor(record.status)}>
          {t(
            `cashFlow.status.${getCashFlowStatusText(record.status!).key}`,
            getCashFlowStatusText(record.status!).title,
          )}
        </Tag>
      ),
      filters: Object.values(CashFlowStatus)
        .filter(value => typeof value === 'number')
        .map(status => ({
          text: getCashFlowStatusText(status as CashFlowStatus).title,
          value: status,
        })),
      onFilter: (value: any, record: any) => record.status === value,
    },
    {
      title: t('payments:payment-method', 'Payment Method'),
      key: 'paymentMethod',
      render: (record: any) =>
        record.paymentMethod != null && (
          <Space>
            <FeatherIcon icon={getPaymentMethodIcon(record.paymentMethod)} size={16} />
            <span>
              {record.paymentMethod === PaymentMethod.Cash
                ? 'Cash'
                : record.paymentMethod === PaymentMethod.Bank
                ? 'Bank Transfer'
                : record.paymentMethod === PaymentMethod.Crypto
                ? 'Crypto'
                : record.paymentMethod === PaymentMethod.Other
                ? 'Other'
                : ''}
            </span>
          </Space>
        ),
    },
    {
      title: t('payments:transaction-reference', 'Reference'),
      dataIndex: 'transactionReference',
      key: 'transactionReference',
      render: (ref: string) => truncate(ref || '', 20) || '-',
    },
    {
      title: t('payments:notes', 'Notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
  ];

  return (
    <>
      <Table
        dataSource={payments}
        columns={[...columns, actionColumn]}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={t('payments:delete-payment-title', 'Delete Payment')}
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteReason('');
          setSelectedPaymentId(null);
        }}
        confirmLoading={isDeleting}
        okText={t('global:delete', 'Delete')}
        cancelText={t('global:cancel', 'Cancel')}
        okButtonProps={{
          danger: true,
        }}
        onOk={handleDeletePayment}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            type="warning"
            message={t('payments:delete-warning', 'This action cannot be undone')}
            style={{ marginBottom: 16 }}
          />
          <Form layout="vertical">
            <Form.Item
              label={t('payments:delete-reason', 'Reason for deletion')}
              help={t('payments:delete-reason-label', 'Please provide a reason for deleting this payment (optional)')}
            >
              <Input.TextArea
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                rows={4}
                placeholder={t('payments:delete-reason-placeholder', 'Enter the reason for deletion...')}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default PaymentSchedule;
